export interface UrgentShiftRewardsSummary {
  completedCount: number;
  goalShifts: number;
  reachedMilestones: number[];
  nextMilestone: number | null;
  shiftsUntilNext: number | null;
  /** T2-6995 primary flag; shiftSaviour kept briefly as dual-emit alias from API */
  shiftHero: boolean;
  shiftSaviour?: boolean;
  footerMessage: string;
}

export interface MyStatsResponse {
  hygienistId: number;
  name: string;
  completedJobs: number;
  cancellations: number;
  totalEarned: number;
  joinedDate: string | null;
  graduationYear: number | null;
  school: string | null;
  affiliate: { code: string; shareUrl: string } | null;
  ratedJobs: number;
  lifetimeStarRating: number;
  totalCancellationsLifetime: number;
  cancellationsAffectingAccount: number;
  ignoredCancellations: number;
  shortNoticeCancellations: number;
  gracePeriodsAvailable: number;
  reliabilityPercentile: number | null;
  reliabilityLabel: string | null;
  feedbackPercentile: number | null;
  feedbackLabel: string | null;
  membershipStatusCode: string | null;
  membershipDisplayLabel?: string | null;
  isProvisional?: boolean;
  metricsUnlock?: { goalShifts: number; completedShifts: number };
  remainingShiftsToUnlock?: number;
  newMemberReward?: { show: boolean; goalShifts: number; completedShifts: number; daysRemaining: number };
  urgentShiftRewards?: UrgentShiftRewardsSummary | null;
  reliability?: { totalCancellations: number; totalShortNoticeCancellations: number; totalGracePeriodsAvailable: number };
  feedback?: { categories: Array<{ name: string; score: number | null }> };
}

export function getPercentileLabelColor(label: string | null): string {
  switch (label) {
    case 'Outstanding': return '#13CA82';
    case 'Very Good': return '#13CA82';
    case 'Great': return '#13CA82';
    case 'Good': return '#0B4598';
    case 'Fair': return '#F59E0B';
    case 'Low': return '#FB7672';
    default: return '#9e9e9e';
  }
}

export function formatOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function getRoleLabel(type: number | undefined): string {
  switch (type) {
    case 0: return 'Dental Hygienist';
    case 1: return 'Dental Assistant';
    case 2: return 'Administrative Staff';
    case 3: return 'Associate Dentist';
    default: return 'Dental Professional';
  }
}

export function getStatusBadgeColors(statusLabel: string): { bg: string; color: string } {
  switch (statusLabel) {
    case 'Elite': return { bg: '#0B4598', color: '#fff' };
    case 'Pro': return { bg: '#0B4598', color: '#fff' };
    case 'Active': return { bg: '#13CA82', color: '#fff' };
    case 'Provisional': return { bg: '#FB7672', color: '#fff' };
    case 'Temporary Limited':
    case 'Limited': return { bg: '#F59E0B', color: '#fff' };
    case 'On a Break': return { bg: '#F59E0B', color: '#fff' };
    case 'Course Required':
    case 'Under Review': return { bg: '#F59E0B', color: '#fff' };
    case 'Account Suspended':
    case 'Suspended': return { bg: '#FB7672', color: '#fff' };
    default: return { bg: '#0B4598', color: '#fff' };
  }
}

/**
 * Maps internal FFS status codes to user-facing labels.
 * Raw codes like RL1, FL2, CourseRequired should never be shown to users.
 */
export function ffsCodeToDisplayLabel(code: string | null): string {
  if (!code) return 'Active';
  switch (code) {
    case 'Elite': return 'Elite';
    case 'Pro': return 'Pro';
    case 'Active': return 'Active';
    case 'RL1':
    case 'FL1':
    case 'RL2':
    case 'FL2':
    case 'RL1FL1':
    case 'RL2FL1':
    case 'RL1FL2':
    case 'RL2FL2': return 'Temporary Limited';
    case 'Provisional': return 'Provisional';
    case 'Probation': return 'Temporary Limited';
    case 'CourseRequired': return 'Course Required';
    case 'Revoked':
    case 'Suspended': return 'Suspended';
    default: return 'Active';
  }
}
